import { Component, OnInit, Input, EventEmitter, Output, ElementRef, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import moment_ from 'moment';
const moment = moment_;

import { IStartEndTimeWindowSchema, WeekDay, IAvailabilityWindowSchema, TimeAfterPipe, IDropdown } from 'common-lib';

// TODO-COMMON
import { TimeRangeService } from '../../../services/time-range/time-range.service';

declare var $: any;
@Component({
    selector: 'common-time-range',
    templateUrl: './time-range.component.html',
    styleUrls: ['./time-range.component.scss'],
    providers: [TimeAfterPipe]
})
export class TimeRangeComponent implements OnInit {
    @Input() index = 0;
    @Input() weekday: WeekDay = 'monday';
    @Input() timeRange: { start: string; end: string };
    @Input() timeIntervals: IDropdown[] = [];
    @Input() meetingLength = 60;
    @Input() startEndTimeWindows: IStartEndTimeWindowSchema[] = [];
    @Output() onUpdateRange = new EventEmitter();
    @Output() onRemoveRange = new EventEmitter();
    @ViewChild('timeRangeStartDropdown', { static: true }) timeRangeStartDropdown: ElementRef;
    @ViewChild('timeRangeEndDropdown', { static: true }) timeRangeEndDropdown: ElementRef;
    /** must be in HH:mm format */
    public minStartHour: string | undefined = '';
    public error = '';
    public isLoading = true;
    private timeRangesSubscription: Subscription;

    constructor(private timeAfterPipe: TimeAfterPipe, private timeRangeService: TimeRangeService) {}

    ngOnInit() {
        this.minStartHour = this.getMinStartHour();
        this.subscribeToTimeRanges();
    }

    ngAfterViewInit() {
        setTimeout(() => {
            this.initDropdowns();
        }, 0); // dropdown will not init properly if page is refreshed without timeout
    }

    ngOnDestroy() {
        this.timeRangesSubscription.unsubscribe();
    }

    private subscribeToTimeRanges() {
        this.timeRangesSubscription = this.timeRangeService.timeRanges$.subscribe(
            (event: { availabilityWindows: IAvailabilityWindowSchema; weekday: WeekDay }) => {
                if (!event || event.weekday !== this.weekday) return;

                const timeRangesForDay = event.availabilityWindows[this.weekday];

                const previousTimeRange = this.index === 0 ? undefined : timeRangesForDay[this.index - 1];
                if (previousTimeRange) {
                    this.minStartHour = previousTimeRange.end;
                }

                const momentMinStartHour = moment(this.minStartHour, 'HH:mm').add(29, 'minutes');
                const momentStartHour = moment(this.timeRange.start, 'HH:mm');

                if (momentMinStartHour.isSameOrAfter(momentStartHour)) {
                    this.error = 'Please update time ranges to be at least 30 minutes apart';
                } else {
                    this.error = '';
                }
            }
        );
    }

    private initDropdowns() {
        const { start, end } = this.timeRange;

        const startTimes = this.formatTimesWithDefaultValue(this.timeIntervals, start);
        $(this.timeRangeStartDropdown.nativeElement).dropdown({
            values: startTimes,
            onChange: (value) => {
                if (value && value !== start) {
                    this.handleStartTimeRangeChange(value, this.meetingLength);
                }
            }
        });

        const endTimes = this.updateEndTimesAfterStartTimes(end);
        const endHour = end;
        $(this.timeRangeEndDropdown.nativeElement).dropdown({
            values: endTimes,
            onChange: (value: string) => {
                if (value && value !== endHour) {
                    this.setRangeEndHour(value);
                }
            }
        });

        this.isLoading = false;
    }

    private getMinStartHour() {
        const previousTimeRange = this.index === 0 ? undefined : this.startEndTimeWindows[this.index - 1];
        return previousTimeRange?.end || '00:00';
    }

    private formatTimesWithDefaultValue(calculatedTimes: IDropdown[], defaultValue: string) {
        return calculatedTimes.map((time) => {
            return {
                ...time,
                selected: time.value === defaultValue
            };
        });
    }

    private setRangeStartHour(value: string) {
        this.timeRange.start = value;
        this.onUpdateRange.emit({
            index: this.index,
            start: value,
            weekday: this.weekday
        });

        if (moment(this.timeRange.start, 'HH:mm').isAfter(moment(this.minStartHour, 'HH:mm'))) this.error = '';
    }

    private setRangeEndHour(value: string) {
        this.timeRange.end = value;
        this.onUpdateRange.emit({
            index: this.index,
            end: value,
            weekday: this.weekday
        });
    }

    /** Filter all end times so they only appear after start time. Update end time if it's too close or overlaps with start time */
    private updateEndTimesAfterStartTimes(endHour: string) {
        const calculatedEndTimes = this.timeAfterPipe.transform(
            this.timeIntervals,
            this.timeRange.start,
            this.meetingLength
        );

        return this.formatTimesWithDefaultValue(calculatedEndTimes, endHour);
    }

    public handleStartTimeRangeChange(value: string, intervalInMins: number) {
        this.setRangeStartHour(value);

        const startingTime = moment(value, 'HH:mm');
        const endingTime = moment(this.timeRange.end, 'HH:mm');
        const isStartTimeAfterEndTime = startingTime.isSameOrAfter(endingTime);
        const isStartTimeCloseToEndTime = moment(startingTime).add(intervalInMins, 'minutes').isSameOrAfter(endingTime);
        const isStartTimeCloseToMidnight = moment(startingTime)
            .add(intervalInMins, 'minutes')
            .isSameOrAfter(moment().endOf('day'));

        let updatedEndTimes = [];

        if (isStartTimeAfterEndTime && isStartTimeCloseToMidnight) {
            updatedEndTimes = this.updateEndTimesAfterStartTimes('24:00');
        } else if (isStartTimeAfterEndTime || isStartTimeCloseToEndTime) {
            let endTime = startingTime.add(intervalInMins, 'minutes').format('HH:mm');
            const isEndTimeAfterMidnight = moment(endTime, 'HH:mm').isSameOrAfter(startingTime.endOf('day'));

            if (endTime === '00:00' || isEndTimeAfterMidnight) {
                endTime = '24:00';
            }
            updatedEndTimes = this.updateEndTimesAfterStartTimes(endTime);
        } else {
            updatedEndTimes = this.updateEndTimesAfterStartTimes(this.timeRange.end);
        }

        $(this.timeRangeEndDropdown.nativeElement).dropdown('change values', updatedEndTimes);
    }

    public handleRemoveTimeRange() {
        this.onRemoveRange.emit({
            weekday: this.weekday,
            start: this.timeRange.start,
            end: this.timeRange.end,
            timeRangeIndex: this.index
        });
    }
}
